import {
    ApartmentOutlined,
    CheckCircleFilled,
    DeleteFilled,
    EyeInvisibleOutlined,
    InboxOutlined,
    InfoCircleOutlined,
    MergeCellsOutlined,
    MergeOutlined,
    OneToOneOutlined,
    PieChartOutlined,
    ProfileTwoTone,
    StarFilled,
    WarningFilled,
} from "@ant-design/icons";
import { Button, Dropdown, Pagination, Spin, Tooltip, Typography } from "antd";
import BadgeIndicator, { IBadgeIndicatorColor } from "components/atoms/BadgeIndicator";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DuplicateDataObjectJobMetaData from "../DuplicateDataObjectJobMetaData";

interface IDuplicateDataObjectRecordClusteredViewProps {
    job: IDataObjectsDuplicateJob;
    dataTags: string[];
    dataObjectList: IDataObjectRecord[];
    loadingMore?: boolean;
    onSelectGroupId: (groupId: string | null) => void;
    onShowSummaryClick: (job: IDataObjectsDuplicateJob) => void;
    onRemoveSurvivorNomination: (obj: IDataObjectRecord) => void;
    onPreviewClick: (obj: IDataObjectRecord) => void;
    onMergeRulesClick: (obj: IDataObjectRecord, group: IDataObjectRecord[]) => void;
}

export default function DuplicateDataObjectRecordClusteredView({
    job,
    dataTags,
    dataObjectList,
    loadingMore,
    onSelectGroupId,
    onShowSummaryClick,
    onRemoveSurvivorNomination,
    onPreviewClick,
    onMergeRulesClick,
}: IDuplicateDataObjectRecordClusteredViewProps) {
    const [filteredDataObjectList, setFilteredDataObjectList] = React.useState<IDataObjectRecord[]>();
    const [paginatedDataObjectList, setPaginatedDataObjectList] = React.useState<IDataObjectRecord[]>();
    const [showJobMetaData, setShowJobMetaData] = React.useState<boolean>(false);
    const [pagination, setPagination] = React.useState<IPagination>({
        current: 1,
        pageSize: 12, // In threes
        page: 1,
    });

    const getProgressBadge = React.useCallback((dataObject: IDataObjectRecord) => {
        let progressFeedback: React.ReactNode;
        let color: IBadgeIndicatorColor = "default";
        let text: string = "";

        switch (dataObject._nominationResult) {
            case "IN_PROGRESS":
                progressFeedback = <Spin size="small"></Spin>;
                text = "Nomination save in progress";
                color = "yellow";
                break;
            case "SUCCESS":
                progressFeedback = <CheckCircleFilled style={{ color: "green" }} />;
                text = "Record nomination saved";
                color = "green";
                break;
            case "FAILED":
                progressFeedback = <WarningFilled style={{ color: "red" }} />;
                text = "Record nomination failed";
                color = "red";
                break;
            case "REMOVE_IN_PROGRESS":
                progressFeedback = <Spin size="small"></Spin>;
                text = "Nomination removal in progress";
                color = "yellow";
                break;
            default:
                return <></>;
        }

        return (
            <BadgeIndicator color={color}>
                <div className="flex items-center gap-1">
                    {text} {progressFeedback}
                </div>
            </BadgeIndicator>
        );
    }, []);

    React.useEffect(() => {
        setFilteredDataObjectList(
            dataObjectList.filter((dataObj) => {
                if (dataObj._groupSize === 1) {
                    return false;
                }

                if (dataObj._survivor) {
                    // Return survivor as first choice
                    return true;
                } else if (dataObj._deleteDuplicates || dataObj._mergeDuplicates) {
                    // Do not return those marked to delete or merge
                    return false;
                }

                // All other dataset
                return dataObj._groupSequence === 1;
            }),
        );
    }, [dataObjectList]);

    React.useEffect(() => {
        if (filteredDataObjectList) {
            setPaginatedDataObjectList(filteredDataObjectList.slice((pagination.current - 1) * pagination.pageSize, pagination.current * pagination.pageSize));
        }
    }, [filteredDataObjectList, pagination]);

    return (
        <div className="flex flex-1 flex-col gap-3 overflow-auto" data-testid={CypressTestIds.DUPLICATE_DATAOBJECT_CONTAINER}>
            <div className="flex w-full gap-3 overflow-hidden rounded-md border border-slate-100 bg-slate-50 p-4">
                <div className="flex flex-col">
                    <h2 className="text-xl font-bold">{job.name}</h2>
                    {job.description && <h3 className="text-xs">{job.description}</h3>}
                </div>
                <div className="flex items-center">{loadingMore ? <Spin className="inline-spin-text" tip="Getting all matching records..."></Spin> : <></>}</div>
                <div className="flex flex-1 items-end justify-end gap-2">
                    <Button type="dashed" onClick={() => onShowSummaryClick(job)} icon={<PieChartOutlined />}>
                        Show Job Summary
                    </Button>
                    <Tooltip placement="topRight" title={showJobMetaData ? "Hide job metadata" : "View job metadata"}>
                        <Button type="dashed" onClick={() => setShowJobMetaData((prev) => !prev)} icon={showJobMetaData ? <EyeInvisibleOutlined /> : <InfoCircleOutlined />}>
                            Job Metadata
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <DuplicateDataObjectJobMetaData job={job} show={showJobMetaData} />
            <div className="scroll-webkit flex flex-1 flex-col overflow-auto pb-2 pl-1 pr-2 pt-1">
                {paginatedDataObjectList && paginatedDataObjectList?.length > 0 ? (
                    <>
                        <div className="grid w-full flex-1 grid-cols-3 gap-3">
                            {paginatedDataObjectList.map((dataObject) => (
                                <div
                                    key={dataObject._dataObjectId}
                                    onClick={() => onSelectGroupId(dataObject._groupId)}
                                    className="cluster-card view-cluster-btn flex max-h-[300px] cursor-pointer flex-col justify-between gap-1 rounded-xl border border-gray-200 bg-white p-6 shadow-md transition-shadow duration-300 hover:bg-slate-100 hover:shadow-lg"
                                >
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-center gap-2">
                                            <h4 className="text-xl font-bold leading-none text-gray-800">
                                                {dataObject._groupSize > 1 ? <ApartmentOutlined style={{ color: "orange" }} /> : <OneToOneOutlined style={{ color: "green" }} />}{" "}
                                                {dataObject._groupId}
                                            </h4>
                                            <div className="flex flex-1 items-center gap-2">
                                                {dataObject._survivor && (
                                                    <Tooltip title="Click to remove nomination">
                                                        <Button
                                                            icon={dataObject._deduplicationOption === "DELETE_DUPLICATES" ? <DeleteFilled /> : <StarFilled />}
                                                            size="small"
                                                            type="primary"
                                                            style={{ background: dataObject._deduplicationOption === "DELETE_DUPLICATES" ? "red" : "green" }}
                                                            disabled={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                            onClick={(e) => {
                                                                e.stopPropagation && e.stopPropagation();
                                                                onRemoveSurvivorNomination(dataObject);
                                                            }}
                                                            loading={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                        ></Button>
                                                    </Tooltip>
                                                )}
                                                {dataObject._survivor && dataObject._deduplicationOption === "MERGE_DUPLICATES" && (
                                                    <Dropdown
                                                        menu={{
                                                            items: [
                                                                {
                                                                    label: "Preview record merge",
                                                                    key: "PREVIEW_RECORD_MERGE",
                                                                    disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                                    icon: (
                                                                        <ProfileTwoTone
                                                                            disabled={dataObject._nominationResult === "REMOVE_IN_PROGRESS"}
                                                                            style={{ color: "yellow", cursor: "pointer" }}
                                                                        />
                                                                    ),
                                                                    onClick: (info) => {
                                                                        info.domEvent?.stopPropagation && info.domEvent.stopPropagation();
                                                                        info.domEvent?.preventDefault && info.domEvent.preventDefault();
                                                                        onPreviewClick(dataObject);
                                                                    },
                                                                },
                                                                {
                                                                    label: "Merge rules",
                                                                    key: "MERGE_RULES",
                                                                    disabled: dataObject._nominationResult === "REMOVE_IN_PROGRESS",
                                                                    icon: <MergeCellsOutlined />,
                                                                    onClick: (info) => {
                                                                        info.domEvent?.stopPropagation && info.domEvent.stopPropagation();
                                                                        info.domEvent?.preventDefault && info.domEvent.preventDefault();
                                                                        onMergeRulesClick(
                                                                            dataObject,
                                                                            dataObjectList.filter((item) => item._groupId === dataObject._groupId),
                                                                        );
                                                                    },
                                                                },
                                                            ],
                                                        }}
                                                    >
                                                        <Button
                                                            icon={<MergeOutlined />}
                                                            type="dashed"
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation && e.stopPropagation();
                                                                e.preventDefault && e.preventDefault();
                                                            }}
                                                        >
                                                            Merge Rules
                                                        </Button>
                                                    </Dropdown>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-1 text-xs">
                                            <div className="font-bold">Duplicate Count:</div>
                                            <div>{dataObject._groupSize}</div>
                                        </div>
                                        {dataTags.map((tag, tagIndex) => (
                                            <div key={`${tag}-${tagIndex}`} className="flex flex-col gap-1">
                                                <div>
                                                    <span className="text-nowrap rounded-sm border border-yellow-200 bg-yellow-100 px-1 text-xs text-yellow-800">{tag}</span>
                                                </div>
                                                {dataObject._tags && dataObject._tags[tag] && dataObject._tags[tag].length > 0 ? (
                                                    dataObject._tags[tag].map((field) => {
                                                        return (
                                                            <div className="flex flex-wrap gap-2" key={field.fieldId}>
                                                                <div>
                                                                    <span className="text-nowrap rounded-sm border border-slate-200 bg-slate-100 px-1 text-xs text-gray-600">
                                                                        {field.name}
                                                                    </span>
                                                                </div>
                                                                <span className="text-nowrap">{field.value}</span>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {!!dataObject._nominationResult && <div className="mt-2 flex items-center justify-between">{getProgressBadge(dataObject)}</div>}
                                </div>
                            ))}
                        </div>
                        <div className="empty-separator h-12 w-full"></div>
                    </>
                ) : (
                    <div className="flex w-full flex-1 flex-col items-center justify-center gap-3 rounded-md bg-gray-50 text-gray-400">
                        <InboxOutlined className="text-4xl" />
                        <div className="flex flex-col items-center">
                            <Typography className="text-gray-400">No cluster found.</Typography>
                            <Typography className="text-xs text-gray-400"> You may want to go to sequence list to view singletons.</Typography>
                        </div>
                    </div>
                )}
            </div>
            {filteredDataObjectList && filteredDataObjectList.length > 0 && (
                <div className="flex justify-end overflow-hidden">
                    <Pagination
                        className="data-table-pagination gap-2"
                        size="small"
                        defaultCurrent={1}
                        current={pagination.current}
                        total={filteredDataObjectList.length}
                        pageSize={pagination.pageSize}
                        showTotal={(total, range) => (
                            <div>
                                {range[0]} - {range[1]} of total {total}
                            </div>
                        )}
                        showSizeChanger={false}
                        showQuickJumper={false}
                        onChange={(page, pageSize) => setPagination((prev) => ({ ...prev, current: page, pageSize: pageSize }))}
                    />
                </div>
            )}
        </div>
    );
}
